import type { StateTree, StoreGeneric } from 'pinia';
import type { AsyncComponentLoader, Component } from 'vue';
import { AnyObjectSchema, AnySchema } from 'yup';
import type {
  ClientResourceKeysNestedAsPath,
  ClientResourceKeysWithNested,
  Role,
} from '@/features/Clients';
import type {
  ComparisonControlsProps,
  ComparisonDetails,
  ComparisonDisabledControls,
  ComparisonHeaderProps,
  ComparisonServiceField,
  ComparisonTariffFeatureProps,
  ComparisonTariffFeaturesProps,
  ComparisonTariffInfoProps,
  ComparisonTariffServicesProps,
} from '@/features/Comparison/Components/types';
import type { FeatureGroup } from '@/features/Features';
import type {
  SEARCH_DATA_CONTEXT,
  SearchDataAllTimeTariffs,
  Sparte,
} from '@/features/Global/types';
import type { LegacyQuoteContent, QuoteContent } from '@/features/Quotes/types';
import type { SORTABLE_ATTRIBUTES } from '@/features/Tariffs/sorting';
import type { STEP } from '@/router/steps';
import type { Company } from '@/features/Companies/types';
import type { InputMappingGroup } from '@/features/InputMapping/types';
import {
  ENTITY_TYPE,
  EntityResourceContainer,
} from '@/features/EntityManager/types';
import { PREVIOUS_INSURANCE_MISS_MATCH_TYPE } from '@/features/PreviousInsurance/Components/ContractDatesMissMatchInfo/types';
import { LocalStorageState } from '@/utils/useLocalStorage/types';
import { TARIFF_SEARCH_REQUEST_IDENTIFIER } from '@/features/Tariffs/types';
import {
  VersicherungsangabenApiContent,
  VersicherungsangabenConfig,
} from '@/features/Versicherungsangaben/types';
import type { ConditionalRequestDefinition } from '@/utils/dehydrateRequestData/types';

interface CustomComparisonComponents {
  ControlsComponent?: AsyncComponentLoader<Component<ComparisonControlsProps>>;

  FeatureComponent?: AsyncComponentLoader<
    Component<ComparisonTariffFeatureProps>
  >;

  GeneralTariffInfoComponent?: AsyncComponentLoader<
    Component<ComparisonTariffInfoProps>
  >;

  HeaderComponent?: AsyncComponentLoader<Component<ComparisonHeaderProps>>;

  TariffFeaturesComponent?: AsyncComponentLoader<
    Component<ComparisonTariffFeaturesProps>
  >;

  TariffServiceComponent?: AsyncComponentLoader<
    Component<ComparisonTariffServicesProps>
  >;
}

export interface SpartenDevToolAction {
  icon: string;
  setup: () => Promise<void | string[]>;
}

export type SpartenDevToolStepConfiguration = {
  [key in STEP]?: SpartenDevToolAction;
};

export interface SpartenDevToolsConfiguration {
  directAccess: SpartenDevToolStepConfiguration;
}

export type CalculationRelevantEntityFields = {
  [k in ENTITY_TYPE]?: {
    [k2 in Role]?: string[];
  };
};

export interface SpartenConfigBaseLayoutProps {
  // name of the vergleichsrechner
  name: string;
  // abbreviation of the Spartenname
  sparte: Sparte;
  // header image as binary import
  image: unknown;
}

export type DocumentTitlePrefixFunc = (
  setTitleCb: (prefix: string) => void,
) => void;

export interface KeepDataFromQuoteMapping {
  path: string;
  formatter: CallableFunction;
  skipIfQuoteValueIsOneOf?: any[];
}
export type KeepDataFromQuoteRoleDefinition = {
  [key in Role]?: KeepDataFromQuoteMapping[];
};

export interface SpartenConfigGeneral<
  U = Record<string, unknown>,
  PF = string,
  QC extends EntityResourceContainer = EntityResourceContainer,
> {
  // distribution settings
  distribution?: {
    /**
     * sparte supports channeling:
     * creating contract via different distribution
     * channel which are just other companies
     */
    channel?: boolean;
    /**
     * sparte supports pooling:
     * contracts can be created via 3rd party pools
     * like JDC or qualitypool
     */
    pooling?: boolean;
  };
  // number of request send when searching tariffs
  // use -1 to disallow simultaneous requests
  maxSimultaneousCount: number;
  // KFZ version sent as konfigurationsParameter.kfzVersion (e.g. 2 for campv2); only used to
  // separate the berechnungsId cache namespace. The V1/V2 dispatch is decided by the compiled
  // campv2 container variant / separate tariff route, not by this value. Omit for unversioned
  // sparten.
  kfzVersion?: number;
  // client data relevant for offer calculation
  clientFields: {
    [k in Role]?: Partial<
      Record<ClientResourceKeysWithNested, ClientResourceKeysNestedAsPath>
    >;
  };
  // define calculation relevant entity fields per entity type to generate comparable hashes
  calculationRelevantEntityFields:
    | CalculationRelevantEntityFields
    | (() => CalculationRelevantEntityFields);
  // define optional client entity dehydration by role
  clientEntityDehydration?: {
    [k in Role]?: ConditionalRequestDefinition<any>[];
  };
  // build input data object per sparte so we can customize the logic or filter data
  buildInputDataObject: (
    context: SEARCH_DATA_CONTEXT,
    dehydrate?: boolean,
  ) => Record<string, unknown>;
  // build contract data object
  buildContractDataObject: (
    state: Record<string, unknown>,
  ) => Record<string, unknown>;
  // optional custom formatter for allTimeTariffs (previous insurance) - used for storing quotes, preparing searchData & printData
  allTimeTariffTransformationCallback?: (
    isQuoteContext?: boolean,
  ) => SearchDataAllTimeTariffs | StateTree | undefined;
  quoteTransformationCallback?: (quote: LegacyQuoteContent) => QuoteContent;
  // hook will be called after quote data hast been merged to the input
  quotePreInputMergeCallback?: (data: Record<string, unknown>) => typeof data;
  // hook will be called if we found allTimeTariffs data in the quote body
  quoteLoadAllTimeTariffCallback?: (data: Record<string, unknown>) => void;
  // hook will be called before sending quote data to the store endpoint
  quotePreStoreCallback?: (
    data: QuoteContent,
  ) => QuoteContent & Record<string, unknown>;
  // hook will be called after loading quote data into input store (could be used to apply custom data)
  quotePostLoadCallback?: (
    data: QuoteContent<QC> | VersicherungsangabenApiContent<QC>,
  ) => void;
  // profile field for form highlighting
  profileFields: PF[];
  // optional array of field paths which will not be overloaded by current PW data
  keepDataFromQuote?: {
    clients?: KeepDataFromQuoteRoleDefinition;
    copyClientFieldsToInputStore?: Record<string, string>; // key = client field path, value = input store field path
  };
  // optional default settings object for user specific settings
  defaultUserSettings?: U;
  // optional additional component for the settings modal
  userSettingsComponent?: AsyncComponentLoader;
  // allows for custom filter function to use in Feature selection
  featureFilterFunction?: (step: 'input' | 'comparison') => FeatureGroup<any>[];
  // sortable attribute definition
  getSortableAttributes?: () => SORTABLE_ATTRIBUTES[];
  // can be used to override features/Tariffs/sorting/sortingGlobals
  getSortableGlobalsOverride?: () => SORTABLE_ATTRIBUTES[];
  // override sorting defaults
  getSortingDefaults?: () => SORTABLE_ATTRIBUTES[];
  // custom comparison components
  comparisonComponents?: CustomComparisonComponents;
  // comparison related settings
  comparison: {
    getDetails: () => ComparisonDetails;
    serviceFields: ComparisonServiceField[];
    disabledComparisonFilters?: ComparisonDisabledControls;
  };
  // define BaseTariff component
  baseTariffComponent: AsyncComponentLoader;
  tariffComponent: AsyncComponentLoader;
  // define store inputs to go to specific steps
  devtools?: SpartenDevToolsConfiguration;
  // Schema to use for VN validation on contract page
  // use function wrapper to avoid import cycles
  contractVnSchema: () => AnyObjectSchema;
  supportMitversichertenPartner?: boolean;
  // optional override to identify if the input matches a partner based relationship
  hasPartner?: () => boolean;
  // optional handler to identify previous insurance
  hasPreviousInsurance?: () => boolean;
  hasAutomaticTermination?: () => boolean;
  // optional definition which previous insurance miss match types are relevant
  relevantPreviousInsuranceMissMatchTypes?: () => PREVIOUS_INSURANCE_MISS_MATCH_TYPE[];
  // custom document title prefix
  documentTitlePrefix?: () => Promise<unknown>;
  // optional callback to identify if we need to ask for bank transfer information on page 4
  requiresBankTransferInformation?: () => boolean;
  // optional callback to identify if we need to ask for payee contact information on page 4
  requiresBankTransferPayeeDetails?: () => boolean;
  // optional callback which will be triggered after loading a session state from local storage
  postLoadingSessionState?: (data: LocalStorageState) => void;
  // optional callback to define alternateTariffSearchRequestIdentifier
  getAlternateTariffSearchRequestIdentifier?: () =>
    | TARIFF_SEARCH_REQUEST_IDENTIFIER
    | undefined;

  // optional custom inventory contract title
  inventoryContractTitle?: () => string;

  // allow Versicherungsbeginn in the past
  isVersicherungsbeginnFromThePastAllowed?: boolean;

  // include eVB Service Modal in base Layout.vue
  enableEVBService?: boolean;

  // optional specified company filter
  specifiedCompanyFilter?: () => Company[];

  // optional feature value manipulation based on given inputStore conditions
  // additionally the feature will be hidden in the feature list on the input page

  // we will test inputStore.$state against schema -> if validates correctly, value will be FEATURE.CHECKED, otherwise FEATURE.UNCHECKED
  overrideFeatureValueWhen?: {
    [key: string]: AnyObjectSchema;
  };

  // optional check on contract step if zipcode and city matches
  verifyZipcodeCityCombination?: boolean;
  // optional input mapping grouping / sorting information
  inputMappingGrouping?: InputMappingGroup[];
  // when have multiple VPs, you must define the relevant roles here / by default, only the global ROLE.VERSICHERTE_PERSON is rated as relevant
  relevantVPRoles?: Partial<Role>[];
  // disable RisikoDEMVer for sparte
  disableRisikoDEMVer?: boolean;
  // backend generates the RisikoDEMVer certificate, so the print option is offered
  hasRisikoDEMVerCertificate?: boolean;
  // Firmen als VN sperren (siehe ClientStore.vnIsBlockedCompany)
  blockCompanyClient?: boolean;
  // optional custom company question component (currently used for UNF)
  companyQuestionComponent?: AsyncComponentLoader;
  // optional PreviousDamage contract override (currently used for RES)
  previousDamageComponent?: AsyncComponentLoader;
  // Versicherungsangaben Configuration
  versicherungsangabenConfig?: VersicherungsangabenConfig;
  // optional Client Entity Meta Schema per Role
  clientEntityMetaSchema?: {
    [key in Role]?: AnySchema;
  };
}

export interface SpartenConfig<U = Record<string, unknown>, PF = string> {
  component: AsyncComponentLoader;
  // url path of sparte
  path: string;
  props: SpartenConfigBaseLayoutProps;
  general: SpartenConfigGeneral<U, PF>;

  // steps configuration
  steps: {
    [key in STEP]: {
      component: AsyncComponentLoader;
      meta?: {
        showContractButton?: boolean;
      };
    };
  };
}

export type SpartenConfigModules = {
  [k in Sparte]: SpartenConfig;
};

export type InputStoreRegistry = {
  [key in Sparte]: () => StoreGeneric;
};

export type InputSchemaRegistry = {
  [key in Sparte]: () => AnyObjectSchema;
};

export type ContractStoreRegistry = {
  [key in Sparte]: () => StoreGeneric;
};

export type ContractSchemaRegistry = {
  [key in Sparte]: () => AnyObjectSchema;
};

export enum WEBREPORT_TYPE {
  COMPARISON = 'comparison',
  OFFERS = 'offers',
}
export interface WebreportsConfiguration {
  type: WEBREPORT_TYPE;
  component: AsyncComponentLoader;
  urlPath: string;
}
